import { FrameBuffer, Resampler, STT_SAMPLE_RATE, floatToInt16 } from "./pcm.js";

/** Thrown when the user (or the browser) refuses microphone access. */
export class MicDeniedError extends Error {
  override readonly name = "MicDeniedError";
}

export interface MicCaptureOptions {
  /** Receives 16 kHz mono `pcm_s16le` frames of `frameMs` each (the last one may be shorter). */
  onFrame: (frame: Int16Array) => void;
  /** Frame length in milliseconds. Default 30. */
  frameMs?: number;
  mediaDevices?: Pick<MediaDevices, "getUserMedia">;
  AudioContext?: typeof AudioContext;
}

export interface MicCapture {
  start(): Promise<void>;
  /** Stops capture, emits the last partial frame and releases the microphone. */
  stop(): Promise<void>;
  readonly active: boolean;
}

/** Minimal AudioWorklet that forwards the first input channel to the main thread. */
const WORKLET_SOURCE = `
class UzCapture extends AudioWorkletProcessor {
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch && ch.length) this.port.postMessage(ch.slice(0));
    return true;
  }
}
registerProcessor("uz-capture", UzCapture);
`;

/**
 * Push-to-talk microphone capture (`getUserMedia`, which needs HTTPS or localhost). Audio goes
 * through an AudioWorklet when available, else a ScriptProcessor, then is resampled to 16 kHz
 * mono 16-bit PCM for the voice service.
 */
export function createMicCapture(opts: MicCaptureOptions): MicCapture {
  const frameMs = opts.frameMs ?? 30;
  let ctx: AudioContext | null = null;
  let stream: MediaStream | null = null;
  let nodes: AudioNode[] = [];
  let frames: FrameBuffer | null = null;
  let resampler: Resampler | null = null;
  let active = false;

  const onSamples = (input: Float32Array) => {
    if (!active || !resampler || !frames) return;
    frames.push(floatToInt16(resampler.push(input)), opts.onFrame);
  };

  return {
    get active() {
      return active;
    },
    async start() {
      if (active) return;
      const devices = opts.mediaDevices ?? globalThis.navigator?.mediaDevices;
      const Ctx = opts.AudioContext ?? globalThis.AudioContext;
      if (!devices?.getUserMedia || !Ctx) throw new MicDeniedError("microphone unavailable");
      try {
        stream = await devices.getUserMedia({
          audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
        });
      } catch (err) {
        throw new MicDeniedError(err instanceof Error ? err.message : "microphone denied");
      }
      ctx = new Ctx();
      resampler = new Resampler(ctx.sampleRate, STT_SAMPLE_RATE);
      frames = new FrameBuffer(Math.round((STT_SAMPLE_RATE * frameMs) / 1000));
      const source = ctx.createMediaStreamSource(stream);
      active = true;
      let worklet: AudioWorkletNode | null = null;
      if (ctx.audioWorklet && typeof AudioWorkletNode !== "undefined") {
        try {
          const url = URL.createObjectURL(
            new Blob([WORKLET_SOURCE], { type: "application/javascript" }),
          );
          await ctx.audioWorklet.addModule(url);
          URL.revokeObjectURL(url);
          worklet = new AudioWorkletNode(ctx, "uz-capture");
          worklet.port.onmessage = (ev: MessageEvent<Float32Array>) => onSamples(ev.data);
        } catch {
          worklet = null;
        }
      }
      if (worklet) {
        source.connect(worklet);
        nodes = [source, worklet];
      } else {
        // Deprecated but universally available fallback.
        const processor = ctx.createScriptProcessor(2048, 1, 1);
        processor.onaudioprocess = (ev) => onSamples(ev.inputBuffer.getChannelData(0));
        source.connect(processor);
        processor.connect(ctx.destination);
        nodes = [source, processor];
      }
    },
    async stop() {
      if (!active) return;
      active = false;
      frames?.flush(opts.onFrame);
      for (const n of nodes) n.disconnect();
      nodes = [];
      for (const track of stream?.getTracks() ?? []) track.stop();
      stream = null;
      await ctx?.close().catch(() => undefined);
      ctx = null;
    },
  };
}
